import { Circle, FabricText, Line, Path, Rect } from 'fabric';
import type { FabricObject } from 'fabric';
import type { PrimitiveSpec, TextSpec } from './kits/specs';

/**
 * Primitive-spec assembler — the ONE place kit specs become fabric objects
 * (the fill.ts pattern: a shared fabric-side helper beside the pure logic).
 * Kits under kits/ are pure layout math with no fabric import; recipes call
 * a kit for specs, then this to build the stock primitives they group.
 *
 * Emits ONLY stock fabric types (FabricText / Line / Rect / Path / Circle) —
 * the types.ts HARD RULE — so every assembled micro-object serializes as
 * plain fabric JSON (exportability law).
 *
 * DBLCLICK EDIT TAGS: a TextSpec carrying `param` is stamped with
 *   data: { tableCell: { kind: 'param', key } }
 * — the smart-table live-edit tag vocabulary extended with a 'param' kind
 * (editor: buildTableCellParams). The editor's existing dblclick overlay
 * machinery then writes the committed text back to params[key] and
 * regenerates the object in place: one history entry, transform preserved.
 * The tag is inert data — it never affects rendering or export.
 */

/** Build one stock primitive from a spec. */
export function specToFabric(spec: PrimitiveSpec): FabricObject {
  switch (spec.kind) {
    case 'text': {
      const s: TextSpec = spec;
      const text = new FabricText(s.text, {
        left: s.x,
        top: s.y,
        originX: s.originX ?? 'left',
        originY: s.originY ?? 'top',
        fontSize: s.fontSize,
        ...(s.fontFamily ? { fontFamily: s.fontFamily } : {}),
        ...(s.fill !== undefined ? { fill: s.fill } : {}),
        ...(s.fontWeight !== undefined ? { fontWeight: s.fontWeight } : {}),
        ...(s.fontStyle ? { fontStyle: s.fontStyle } : {}),
        ...(s.angle !== undefined ? { angle: s.angle } : {}),
        ...(s.visible !== undefined ? { visible: s.visible } : {}),
      });
      if (s.param) {
        text.set('data', { tableCell: { kind: 'param', key: s.param } });
      }
      return text;
    }
    case 'line':
      return new Line([spec.x1, spec.y1, spec.x2, spec.y2], {
        stroke: spec.stroke,
        strokeWidth: spec.strokeWidth,
        strokeLineCap: spec.lineCap ?? 'butt',
        ...(spec.dash ? { strokeDashArray: spec.dash } : {}),
      });
    case 'rect':
      return new Rect({
        left: spec.x,
        top: spec.y,
        width: spec.width,
        height: spec.height,
        fill: spec.fill ?? '',
        ...(spec.stroke ? { stroke: spec.stroke, strokeWidth: spec.strokeWidth ?? 1 } : {}),
        ...(spec.rx ? { rx: spec.rx, ry: spec.rx } : {}),
        ...(spec.dash ? { strokeDashArray: spec.dash } : {}),
      });
    case 'path':
      return new Path(spec.path, {
        fill: spec.fill ?? '',
        ...(spec.stroke ? { stroke: spec.stroke, strokeWidth: spec.strokeWidth ?? 1 } : {}),
        strokeLineCap: spec.lineCap ?? 'round',
        strokeLineJoin: spec.lineJoin ?? 'round',
      });
    case 'circle':
      return new Circle({
        left: spec.cx,
        top: spec.cy,
        originX: 'center',
        originY: 'center',
        radius: spec.r,
        fill: spec.fill ?? '',
        ...(spec.stroke ? { stroke: spec.stroke, strokeWidth: spec.strokeWidth ?? 1 } : {}),
      });
  }
}

/**
 * Assemble a whole spec array (kit block output, already translated into its
 * final local coordinates) into primitives ready for `new Group(children)` —
 * children carry absolute coords; Group derives the bbox and the caller
 * places it (the convention every recipe follows).
 */
export function assembleSpecs(specs: PrimitiveSpec[]): FabricObject[] {
  return specs.map(specToFabric);
}
